import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '../types/firebase';
import { doc, getDoc } from 'firebase/firestore';
import EnvelopeAnimation from '../components/EnvelopeAnimation';
import Modal from '../components/Modal';
import DetailsSection from '../components/sections/DetailsSection';
import { GuestGroup, ModalState } from '../types/interfaces';
import '../style/animation.css';
import background from '../assets/background1.jpeg';
import Loading from '../components/Loading';

interface HomeProps {
  lang: string;
}

const Home: React.FC<HomeProps> = ({ lang }) => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [guestGroup, setGuestGroup] = useState<GuestGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modalState, setModalState] = useState<ModalState>(ModalState.NONE);
  const [animationReady, setAnimationReady] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  // Set language based on route
  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  // Preload the background image
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBgLoaded(true);
    img.src = background;
  }, []);

  useEffect(() => {
    const fetchGuestGroup = async () => {
      const rsvpCode = searchParams.get('code');
      if (!rsvpCode) {
        setLoading(false);
        return;
      }
  
      try {
        const codeRef = doc(db, 'rsvpCodes', rsvpCode);
        const codeSnap = await getDoc(codeRef);
        if (!codeSnap.exists()) {
          throw new Error('Invalid RSVP code');
        }
        const groupId = codeSnap.data().groupId;
        console.log(groupId)

        const groupRef = doc(db, 'guestGroups', groupId);
        const groupSnap = await getDoc(groupRef);
        if (!groupSnap.exists() || groupSnap.data().rsvpCode !== rsvpCode) {
          throw new Error('RSVP code mismatch');
        }
        const groupData = { id: groupSnap.id, ...groupSnap.data() } as GuestGroup;
        console.log(groupData)
        setGuestGroup(groupData);
      } catch (err) {
        console.error('Error fetching guest group information:', err);
        setError(true);
        setModalState(ModalState.ERROR);
      } finally {
        setLoading(false);
      }
    };

    fetchGuestGroup();
  }, [searchParams]);

  const handleRSVPComplete = (updatedGroup: GuestGroup) => {
    setGuestGroup(updatedGroup);
    const atLeastOneComing = updatedGroup.guests.some(guest => guest.coming);
    setModalState(atLeastOneComing ? ModalState.THANK_YOU : ModalState.SORRY);
  };

  const handleRSVPClick = () => {
    const rsvpCode = searchParams.get('code');
    if (!rsvpCode) {
      console.error('No RSVP code provided in URL');
      setError(true);
      setModalState(ModalState.ERROR);
      return;
    }

    if (!guestGroup) {
      console.error('Guest group information not available');
      setError(true);
      setModalState(ModalState.ERROR);
      return;
    }

    const allGuestsResponded = guestGroup.guests.every(guest => guest.coming !== undefined);
    
    if (allGuestsResponded) {
      const atLeastOneComing = guestGroup.guests.some(guest => guest.coming);
      setModalState(atLeastOneComing ? ModalState.THANK_YOU : ModalState.SORRY);
    } else {
      setModalState(ModalState.RSVP);
    }
  };

  const closeModal = () => {
    setModalState(ModalState.NONE);
    setError(false);
  };

  return (
    <>
      {/* Fullscreen Loading Overlay */}
      {(loading || !animationReady || !bgLoaded) && <Loading fullscreen={true} />}

      <main className="relative">
        <section
          id="home"
          className="min-h-screen"
          style={{
            backgroundImage: `url(${background})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}
        >
          <div className="flex flex-col items-center min-h-screen">
            <EnvelopeAnimation 
              guestName={guestGroup?.groupInvite ?? ''} 
              language={lang === 'it' ? 'it' : 'en'}
              onReady={() => setAnimationReady(true)}
              loadingComplete={!loading && animationReady && bgLoaded}
              onComplete={() => setAnimationComplete(true)}
            />

            <button
              onClick={handleRSVPClick}
              className={`mt-4 md:mt-8 lg:mt-12 xl:mt-20 2xl:mt-28 text-[#2d5a2d] border-[2px] border-[#2d5a2d] font-medium overflow-hidden px-20 py-2 rounded-lg hover:bg-[#2d5a2d] hover:text-[#f5f0e6] active:opacity-75 outline-none duration-300 bg-transparent text-lg tracking-wide uppercase font-sans shadow-lg z-10 transition-opacity ${
                animationComplete ? 'opacity-100 pointer-events-auto animate-pulse-gentle' : 'opacity-0 pointer-events-none'
              }`}
            >
              {t('rsvp_button')}
            </button>
            <p className={`font-body text-[#2d5a2d] text-sm mt-2 z-10 transition-opacity ${
              animationComplete ? 'opacity-100' : 'opacity-0'
            }`}>{t('rsvp_deadline')}</p>

            <DetailsSection />
          </div>
        </section>

        {/* Modal Component */}
        <Modal
          modalState={modalState}
          error={error}
          guestGroup={guestGroup}
          onClose={closeModal}
          onRSVPComplete={handleRSVPComplete}
        />
      </main>
    </>
  );
};

export default Home;
